// src/services/community.service.ts
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { Community } from "../types";

const COLLECTION = "communities";

type NewCommunity = Omit<Community, "id" | "createdAt" | "createdBy">;

/**
 * Create a new community
 */
export const createCommunity = async (
  community: NewCommunity,
  userId: string
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...community,
      createdAt: new Date(),
      createdBy: userId,
    });
    console.log("Community created with ID:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error creating community:", error);
    throw error;
  }
};

/**
 * Get all communities
 */
export const getCommunities = async (): Promise<Community[]> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTION));
    return snapshot.docs.map((d) => {
      const data = d.data() as any;
      return {
        id: d.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
      } as Community;
    });
  } catch (error) {
    console.error("Error fetching communities:", error);
    throw error;
  }
};

/**
 * Get community by Firestore document ID
 */
export const getCommunityById = async (id: string): Promise<Community | null> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;

    const data = docSnap.data() as any;
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
    } as Community;
  } catch (error) {
    console.error("Error fetching community:", error);
    throw error;
  }
};

/**
 * Get community by short ID (BNL, VH, etc)
 */
export const getCommunityByShortId = async (
  shortId: string
): Promise<Community | null> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where("shortId", "==", shortId.toUpperCase())
    );
    const snapshot = await getDocs(q);

    if (snapshot.docs.length === 0) return null;

    const d = snapshot.docs[0];
    const data = d.data() as any;
    return {
      id: d.id,
      ...data,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
    } as Community;
  } catch (error) {
    console.error("Error fetching community by shortId:", error);
    throw error;
  }
};

/**
 * Update an existing community
 */
export const updateCommunity = async (
  id: string,
  updates: Partial<Community>
): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date(),
    });
    console.log("Community updated:", id);
  } catch (error) {
    console.error("Error updating community:", error);
    throw error;
  }
};

/**
 * Delete a community
 */
export const deleteCommunity = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTION, id));
    console.log("Community deleted:", id);
  } catch (error) {
    console.error("Error deleting community:", error);
    throw error;
  }
};
